import { useEffect, useState, type FormEvent } from "react";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { paths, ROLE_RANK, type Shift } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";

type ShiftRow = Shift & { id: string };

export function ShiftBoard() {
  const { tenantId, user, role } = useAuth();
  const isManager = role ? ROLE_RANK[role] >= 2 : false;
  const [shifts, setShifts] = useState<ShiftRow[]>([]);
  const [showPost, setShowPost] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({ title: "", date: "", start: "08:00", end: "17:00" });

  useEffect(() => {
    if (!tenantId) return;
    const q = query(
      collection(db, paths.tenantShifts(tenantId)),
      where("status", "in", ["posted", "claim_pending", "assigned"]),
      orderBy("startAt"),
    );
    return onSnapshot(q, (snap) => {
      setShifts(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Shift) })));
    });
  }, [tenantId]);

  async function postShift(e: FormEvent) {
    e.preventDefault();
    if (!tenantId) return;
    setBusy(true);
    setMessage("");
    try {
      const startAt = new Date(`${form.date}T${form.start}`).getTime();
      const endAt = new Date(`${form.date}T${form.end}`).getTime();
      await call("postShift", { tenantId, title: form.title, startAt, endAt });
      setShowPost(false);
      setForm({ ...form, title: "" });
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function act(fn: string, shiftId: string, extra: Record<string, unknown> = {}) {
    if (!tenantId) return;
    setMessage("");
    try {
      await call(fn, { tenantId, shiftId, ...extra });
    } catch (err) {
      setMessage((err as Error).message);
    }
  }

  function fmtRange(s: ShiftRow) {
    const d = new Date(s.startAt);
    const day = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    const t1 = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    const t2 = new Date(s.endAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return `${day}, ${t1} – ${t2}`;
  }

  const statusStyle: Record<string, string> = {
    posted: "bg-brandorange/15 text-brandorange-dark",
    claim_pending: "bg-steel/20 text-ink",
    assigned: "bg-success/15 text-success",
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Shifts</h1>
        {isManager ? (
          <Button onClick={() => setShowPost((v) => !v)}>{showPost ? "Cancel" : "Post shift"}</Button>
        ) : null}
      </div>

      {showPost ? (
        <Card>
          <CardTitle>Post an open shift</CardTitle>
          <form onSubmit={postShift} className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Title</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Start</Label>
                <Input type="time" required value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
              </div>
              <div>
                <Label>End</Label>
                <Input type="time" required value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} />
              </div>
            </div>
            <div className="col-span-2">
              <Button type="submit" busy={busy}>
                Post to the board
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {message ? <p className="text-xs text-danger">{message}</p> : null}

      <Card className="p-0">
        {shifts.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-3 border-b border-steel/10 px-4 py-3 last:border-0">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-ink">{s.title}</div>
              <div className="text-xs text-steel">{fmtRange(s)}</div>
              {s.claim ? <div className="text-xs text-steel">Claimed by {s.claim.displayName || "a teammate"}</div> : null}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-xs ${statusStyle[s.status] ?? "bg-steel/15"}`}>
                {s.status.replace("_", " ")}
              </span>
              {s.status === "posted" && !isManager ? (
                <Button className="px-3 py-1 text-xs" onClick={() => void act("claimShift", s.id)}>
                  Pick up
                </Button>
              ) : null}
              {s.status === "claim_pending" && isManager ? (
                <>
                  <Button
                    className="px-3 py-1 text-xs"
                    onClick={() => void act("resolveShiftClaim", s.id, { decision: "approve" })}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="ghost"
                    className="px-3 py-1 text-xs"
                    onClick={() => void act("resolveShiftClaim", s.id, { decision: "deny" })}
                  >
                    Deny
                  </Button>
                </>
              ) : null}
              {s.status === "posted" && isManager ? (
                <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => void act("cancelShift", s.id)}>
                  Cancel
                </Button>
              ) : null}
            </div>
          </div>
        ))}
        {shifts.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-steel">
            No open or upcoming shifts. {isManager ? "Post one to fill your schedule." : ""}
          </div>
        ) : null}
      </Card>
      {user ? null : null}
    </div>
  );
}
